import React, { useCallback, useMemo, useRef } from 'react';

import PropTypes from 'prop-types';
import { FaLock } from 'react-icons/fa';
import mergeRefs from 'react-merge-refs';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { HTMLAsset, useAsset } from '@techstyle/react-assets';
import {
  mobile,
  Button,
  getPromoAssetFlags,
  ProductCell as BaseProductCell,
  ProductImage,
  ProductName as BaseProductName,
  ProductPrice as BaseProductPrice,
  ProductReview as BaseProductReview,
  MoreColorsLabel,
  usePromoAssetFilter,
  useTabIndex,
  useProductGridPricingContext,
} from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';
import { ProductType, useProductListViewed } from '@techstyle/react-products';

import { useProductClickTracking } from '../../segment-events/utils';
import { toStringSet } from '../../utils/assets';
import formatProductUrl from '../../utils/formatProductUrl';
import { generateCdnImagesBmig } from '../../utils/generateCdnImageUrl';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import { getIsSoldOut, getIsPreorder } from '../../utils/products';
import {
  getProductPromos,
  getShouldShowPromoPrice,
  isClearanceItem,
  isComingSoonProduct,
  isCurvySize,
  shouldProductUseCustomTag,
} from '../../utils/selectors';
import { useMembership } from '../../utils/useMembership';
import useOrderedModels from '../../utils/useOrderedModels';
import useProductCallout, {
  Callout as CalloutTypes,
} from '../../utils/useProductCallout';
import { useByoContext } from '../ByoProvider';
import { Component as Price } from '../ExtendedPrice';
import { Component as FavoritesToggleButton } from '../FavoritesToggleButton';
import ProductCallout from '../ProductCallout';
import { ProductPrice } from '../ProductPrice/ProductPrice';

const EmptyStar = () => {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 12 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.23885 0.619542L7.06875 3.30485C7.16587 3.61909 7.4564 3.8334 7.78531 3.8334H10.5219C10.7611 3.8334 10.8637 4.13688 10.6737 4.28205L8.41687 6.00621C8.16711 6.19703 8.06282 6.52334 8.15563 6.82364L9.00722 9.57915C9.07909 9.81171 8.81003 9.9994 8.6166 9.85163L6.45528 8.20052C6.18649 7.99518 5.81352 7.99517 5.54472 8.20049L3.38283 9.85181C3.18941 9.99956 2.92036 9.81187 2.99223 9.57932L3.84388 6.82362C3.93668 6.52334 3.83241 6.19704 3.58267 6.00622L1.32614 4.28204C1.13613 4.13687 1.2388 3.8334 1.47792 3.8334H4.21469C4.5436 3.8334 4.83413 3.61909 4.93125 3.30485L5.76115 0.619543C5.83375 0.384633 6.16625 0.384633 6.23885 0.619542Z"
        stroke="black"
        strokeWidth="0.5"
      />
    </svg>
  );
};

const FullStar = () => {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.52229 1.54573C5.66749 1.07591 6.33251 1.07591 6.47771 1.54572L7.3076 4.23103C7.37235 4.44053 7.56604 4.5834 7.78531 4.5834H10.5219C11.0002 4.5834 11.2055 5.19037 10.8255 5.48071L8.56865 7.20487C8.40214 7.33208 8.33261 7.54962 8.39448 7.74983L9.24607 10.5053C9.38982 10.9704 8.85168 11.3458 8.46483 11.0503L6.30351 9.39918C6.12432 9.26229 5.87567 9.26228 5.69647 9.39916L3.53459 11.0505C3.14773 11.346 2.60964 10.9706 2.75337 10.5055L3.60503 7.7498C3.66689 7.54961 3.59738 7.33208 3.43089 7.20487L1.17435 5.48069C0.79434 5.19034 0.999677 4.5834 1.47792 4.5834H4.21469C4.43397 4.5834 4.62765 4.44053 4.6924 4.23103L5.52229 1.54573Z"
        fill="black"
      />
    </svg>
  );
};

const ProductLink = styled(Button).attrs(() => {
  return {
    variant: 'other',
  };
})`
  display: block;
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  margin: 0;
  padding: 0;
  text-align: left;
`;

const StyledBaseProductCell = styled(BaseProductCell)`
  position: relative;
  text-align: left;
  letter-spacing: ${0.3 / 12}em;
  padding-bottom: 25px;

  .tagg-balloons-wrapper.tagg-category-page {
    bottom: 0;

    .tagg-balloon {
      width: 100%;
      border-radius: 0;
      height: ${props => props.theme.spacing.medium}px;
      background-color: ${props => props.theme.colors.lavender200};

      .tagg-txt {
        color: ${props => props.theme.colors.black};
        ${({ theme }) =>
          theme.paragraph.variants.paragraph5Uppercase.textStyles}
        line-height: 12px;
        font-weight: 375;
        letter-spacing: 0.3px;

        strong {
          ${({ theme }) => theme.subHeader.variants.subHeading5.textStyles}
          letter-spacing: 0.3px;
        }
      }

      .tagg-icon {
        filter: brightness(0) saturate(100%);
      }
    }
  }
`;

const ThumbnailWrapper = styled.div`
  width: 100%;
  height: auto;
`;

const StyledProductImage = styled(ProductImage)``;

const ImageOverlay = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing.tiny}px;
  right: ${({ theme }) => theme.spacing.tiny}px;
`;

const SoldOutOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  font-size: 16px;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.white};
  background: ${props => props.theme.colors.lightShadow1};
  pointer-events: none;
`;

const ComingSoonOverlay = styled(SoldOutOverlay)``;

const ProductSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: ${({ theme }) => theme.spacing.tiny}px 0;

  & > *:not(:last-child) {
    margin-bottom: 4px;
  }
  ${mobile`padding-left: ${({ theme }) => theme.spacing.tiny}px;`}
`;

const AdditionalContent = styled.div`
  margin-top: 9px;
`;

const MoreColorsLink = styled(MoreColorsLabel)`
  font-size: 12px;
  line-height: normal;
  color: ${props => props.theme.colors.subdued};

  &&& {
    text-decoration: none;
  }

  ${({ theme }) => theme.paragraph.variants.paragraph5Uppercase.textStyles}
  text-align: left;
`;

const PriceWrapper = styled.div`
  ${({ $gridPdpPricingUpdatesEnabled }) =>
    $gridPdpPricingUpdatesEnabled
      ? css`
          margin-top: 4px;
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
        `
      : css`
          display: inline-block;
          font-weight: 500;
          line-height: 1.14;
        `}

  align-items: baseline;
  margin-bottom: 0;
`;

const CellDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StyledProductPrice = styled(BaseProductPrice)`
  ${({ theme }) => theme.subHeader.variants.subHeading3Uppercase.textStyles}
  text-align: left;
  color: ${({ theme }) => theme.colors.black};
`;

const ProductName = styled(BaseProductName)`
  ${({ theme, $gridPdpPricingUpdatesEnabled }) =>
    $gridPdpPricingUpdatesEnabled
      ? css`
          ${theme.paragraph.variants.paragraph4Uppercase.textStyles}
        `
      : css`
          ${({ theme }) =>
            theme.paragraph.variants.paragraph3Uppercase.textStyles}
          font-weight: 500;
          line-height: 18px;
        `}
  display: block;
  white-space: normal;
  color: ${props => props.theme.colors.black};
  max-width: 100%;
`;

const ProductReview = styled(BaseProductReview)`
  svg {
    color: #1c1b1f;
    height: 12px;
    width: 12px;
  }
`;

const ratingStyle = css`
  & svg {
    color: ${props => props.theme.colors.default};
    height: 13px;
    width: 14px;
  }

  line-height: 0;
`;

const BorderfreePricing = styled.div``;

const USDPrice = styled.span`
  color: ${props => props.theme.colors.default};
  font-weight: 500;
  padding-left: 4px;

  span {
    padding-left: 4px;
  }

  font-size: 14px;
`;

const BorderFreePrice = styled(Price)`
  display: ${props => (props.isBorderfreeCustomer ? `inline-block` : `block`)};
  font-size: 14px;
  font-weight: 500;
  padding: 0 0 2px;
  color: ${({ theme }) => theme.colors.default};
  flex: 1;
`;

const PromoLabel = styled.div`
  margin-bottom: 0;

  color: ${({ theme, isSaleItem }) =>
    isSaleItem ? theme.colors.error : theme.colors.lavender700};

  ${({ $gridPdpPricingUpdatesEnabled, isSaleItem, theme }) => {
    if ($gridPdpPricingUpdatesEnabled) {
      return css`
        ${theme.paragraph.variants.paragraph5Uppercase.textStyles}
        color: ${theme.colors.memberOffers};

        ${PromoMessageAsset} {
          ${theme.paragraph.variants.paragraph5Uppercase.textStyles}
        }
      `;
    }

    return css`
      color: ${isSaleItem ? theme.colors.error : theme.colors.lavender700};
      ${props => props.theme.paragraph.paragraph4Uppercase}
    `;
  }}
`;

const BundlePromoWrapper = styled.span`
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colors.black};

  ${({ $gridPdpPricingUpdatesEnabled, theme }) =>
    $gridPdpPricingUpdatesEnabled
      ? css`
          ${theme.subHeader.variants.subHeading3Uppercase.textStyles}
        `
      : css`
          ${theme.paragraph.variants.paragraph3Uppercase.textStyles}
          font-weight: 500;
          margin-left: 4px;
        `}
`;

const BundlePromoLabel = styled.span`
  ${({ theme }) => theme.paragraph.variants.paragraph3.textStyles}
  color: ${({ theme }) => theme.colors.black};
  font-weight: 500;
`;

const OptionText = styled.span`
  ${({ $gridPdpPricingUpdatesEnabled }) =>
    $gridPdpPricingUpdatesEnabled
      ? css`
          ${({ theme }) =>
            theme.paragraph.variants.paragraph4Uppercase.textStyles}
        `
      : css`
          ${({ theme }) => theme.paragraph.variants.paragraph3.textStyles}
        `}
  color: ${({ theme }) => theme.colors.black};

  margin-right: 4px;
`;

const priceStyle = isSaleItem => css`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) =>
    isSaleItem ? theme.colors.error : theme.colors.default};

  ${({ $showStrikethrough }) =>
    $showStrikethrough &&
    css`
      font-weight: 400;
      color: ${({ theme }) => theme.colors.grey} !important;
    `}
`;

const promoPriceStyle = isSaleItem => css`
  font-size: 14px;
  font-weight: 500;

  color: ${({ theme }) =>
    isSaleItem ? theme.colors.error : theme.colors.black};

  ${({ $showStrikethrough }) =>
    $showStrikethrough &&
    css`
      font-weight: 400;
      color: ${({ theme }) => theme.colors.grey} !important;
    `}
`;

const Callout = styled(ProductCallout)`
  font-size: 12px;
  padding: 4px ${({ theme }) => theme.spacing.tiny}px;

  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 0;
  color: ${({ theme }) => theme.colors.gunmetal};
  position: absolute;
  top: 0;
  left: ${({ theme }) => theme.spacing.tiny}px;
  margin-right: ${({ theme }) => theme.spacing.tiny}px;
`;

const PromoMessageAsset = styled(HTMLAsset)``;

const QuickShopLabel = styled.div`
  background-color: ${({ theme }) => theme.colors.lavender250};
  color: ${({ theme }) => theme.colors.lavender500};
  font-weight: 700;
  font-size: 12px;
  line-height: 14px;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  height: 42px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-top: -14px;
  cursor: pointer;
  ${({ theme }) =>
    css`
      ${theme.button.variants.tertiaryDark.buttonStyle}
    `};
  min-width: 0;
  width: 100%;
  height: 32px;
`;

const LockedProducts = styled.div`
  margin-right: 8px;
  display: inline-block;
  color: ${({ theme }) => theme.colors.white};
`;

const DefaultPromoMessage = React.memo(function DefaultPromoMessage({
  isVip,
  isSet,
}) {
  if (isSet) {
    return isVip ? (
      <FormattedMessage
        id="site_pdp.vip_xclusive_sets_price"
        defaultMessage="VIP XCLUSIVE SET"
      />
    ) : (
      <FormattedMessage
        id="site_pdp.non_vip_sets_price"
        defaultMessage="NEW VIP SET"
      />
    );
  }

  return isVip ? (
    <FormattedMessage
      id="site_pdp.xclusive_vip_price"
      defaultMessage="VIP XCLUSIVE"
    />
  ) : (
    <FormattedMessage
      id="site_pdp.offer_non_vip_price"
      defaultMessage="NEW VIP OFFER"
    />
  );
});

DefaultPromoMessage.displayName = 'DefaultPromoMessage';

DefaultPromoMessage.propTypes = {
  isSet: PropTypes.bool,
  isVip: PropTypes.bool,
};

function renderCallout(
  isProductPreorder,
  shouldProductUseCustom,
  elementsToHide,
  callout
) {
  if (!elementsToHide.flags && shouldProductUseCustom) {
    return <Callout type={CalloutTypes.CUSTOM_TAG} />;
  }

  if (isProductPreorder) {
    return <Callout type={CalloutTypes.PRE_ORDER} />;
  }

  return !elementsToHide.flags && callout ? (
    <Callout type={callout.type} value={callout.value} />
  ) : null;
}

function createPromoAssetFilter({ isBorderfreeCustomer }) {
  return (asset, index, assets) => (asset, index, assets) => {
    if (isBorderfreeCustomer) {
      const flags = getPromoAssetFlags(asset);
      if (flags.has('noBorderfree')) {
        return false;
      }
    }
    return true;
  };
}

const ProductCellBMIG = React.forwardRef(
  (
    {
      additionalContentHref,
      children,
      elementsToHide = {
        price: false,
        colorsAvailable: false,
        flags: false,
        overlay: false,
        reviewStars: false,
        promo: false,
        wishlist: false,
        name: false,
      },
      fplId,
      gridIndex,
      imageAspectRatio,
      imageType,
      isByo = false,
      isCio = false,
      isQuickViewEnabled,
      onProductClick,
      overrideHref,
      payloadKey,
      product,
      productSource,
      setDefaultProductAndOpenQuickViewModal,
      shouldOverrideHref = false,
      shouldShowByoLayout = false,
      shouldShowLaydown,
      shouldShowLocked = false,
      shouldShowShadow,
      shouldShowSpacedBorder,
      tabIndex,
      shouldDisplayNewProductPricing,
      ...rest
    },
    ref
  ) => {
    tabIndex = useTabIndex(tabIndex);
    const {
      'constructor-search': constructorSearchEnabled,
      'grid-pdp-pricing-updates': gridPdpPricingUpdatesEnabled,
    } = useLDFlags();

    const { isVip, isDowngraded, isLead } = useMembership();
    const productGridPricingContext = useProductGridPricingContext();
    const productPriceInfo =
      productGridPricingContext?.productGridPricing?.[product?.masterProductId];

    const {
      isBorderfreeCustomer,
      isComingSoonItem,
      isCurvy,
      isProductPreorder,
      isSaleItem: initialIsSaleItem,
      isSoldOut,
      promos,
      shouldShowPromoPrice,
      shouldProductUseCustom,
    } = useSelector(state => {
      return {
        isBorderfreeCustomer: state.borderfree.isBorderfreeCustomer,
        isComingSoonItem: isComingSoonProduct(state, { product }),
        isCurvy: isCurvySize(state, { product }),
        isProductPreorder: getIsPreorder(state, { product }),
        isSaleItem: isClearanceItem(state, { product }),
        isSoldOut: getIsSoldOut(state, { product }),
        promos:
          Object.keys(
            getProductPromos(state, { product: productPriceInfo || {} })
          ).length !== 0
            ? getProductPromos(state, { product: productPriceInfo || {} })
            : getProductPromos(state, { product }),
        shouldShowPromoPrice:
          getShouldShowPromoPrice(state, { product: productPriceInfo || {} }) ||
          getShouldShowPromoPrice(state, { product }),
        shouldProductUseCustom: shouldProductUseCustomTag(state, { product }),
      };
    });

    const isSaleItem = gridPdpPricingUpdatesEnabled
      ? initialIsSaleItem && isVip
      : false;

    const callout = useProductCallout({ product });
    const { trackClickFromByoGrids } = useByoContext();

    const customPromoAssetFilter = useMemo(
      () => createPromoAssetFilter({ isBorderfreeCustomer }),
      [isBorderfreeCustomer]
    );
    const promoAssetFilter = usePromoAssetFilter({
      product,
      filter: customPromoAssetFilter,
    });

    const trackProductClick = useProductClickTracking();

    const assetContainer = useAsset('pdp_html_fpl', {
      filter: promoAssetFilter,
    });
    const { data } = assetContainer;
    const asset = data ? data.assets[0] : null;

    const { displayPrice } = useMemo(() => {
      const { defaultUnitPrice, retailUnitPrice } = productPriceInfo || product;
      let displayPrice;

      if (isBorderfreeCustomer) {
        displayPrice = retailUnitPrice;
      } else if (shouldShowPromoPrice) {
        displayPrice = promos.promoPrice;
      } else {
        displayPrice = defaultUnitPrice;
      }

      return { displayPrice };
    }, [
      isBorderfreeCustomer,
      product,
      productPriceInfo,
      promos,
      shouldShowPromoPrice,
    ]);

    const isSet = product.productTypeId === ProductType.BUNDLE;
    const { initialModel, isMatchUserSizeFeatureEnabled } = useOrderedModels(
      product,
      isCurvy,
      undefined,
      isSet,
      true
    );

    const href = shouldOverrideHref
      ? overrideHref
      : formatProductUrl({
          permalink: product.permalink,
          productId: product.masterProductId || product.master_product_id,
          productType: isSet ? 'sets' : undefined,
          pSource: productSource,
          extraQueryParams: {
            gridPosition: gridIndex,
          },
        }).as;

    const productInfo = useMemo(
      () => ({
        product,
        isPriceHidden: elementsToHide.price,
        href: href.split(/[#?]/)[0],
        gridIndex,
        fplId,
        productSource,
        payloadKey,
      }),
      [
        fplId,
        gridIndex,
        href,
        productSource,
        payloadKey,
        product,
        elementsToHide.price,
      ]
    );

    const handleClick = useCallback(
      event => {
        if (shouldShowLocked) {
          event.preventDefault();
          return;
        }

        if (onProductClick) {
          onProductClick(product);
        }
        if (shouldShowByoLayout) {
          trackClickFromByoGrids();
        }
        trackProductClick(product, gridIndex, productSource, fplId);
      },
      [
        fplId,
        gridIndex,
        onProductClick,
        product,
        productSource,
        shouldShowByoLayout,
        shouldShowLocked,
        trackClickFromByoGrids,
        trackProductClick,
      ]
    );

    const { observe, unobserve } = useProductListViewed() || {};
    const productCellRef = useRef();

    const updateProductCellRef = useCallback(
      element => {
        // try/catch here because IntersectionObserver (used in the observe/unobserve methods) might not be instantiated yet.
        // The code in ProductListViewedProvider doesn't check if it is or not when observe/unobserve are called.
        // IntersectionObserver depends on a layout effect to happen, which might not have happened.
        // @techstyle/react-products/dist-esm/ProductListViewedProvider/ProductListViewedProvider.js:82
        try {
          const oldNode = productCellRef.current;
          const newNode = element?.nodeType === 1 ? element : null;
          productCellRef.current = newNode;
          if (oldNode !== newNode) {
            if (oldNode && unobserve) {
              unobserve(oldNode);
            }
            if (newNode && observe) {
              observe(newNode, productInfo);
            }
          }
        } catch {
          // IntersectionObserver was probably not instantiated yet
        }
      },
      [observe, productInfo, unobserve]
    );

    const assetFlags = asset
      ? toStringSet(asset.options.customVars.flags, true)
      : new Set();
    const bundle = asset && assetFlags.has('bundle');
    const overlayText =
      (bundle && asset && asset.options.customVars.overlayText) || null;
    const crossOut = shouldShowPromoPrice
      ? false
      : asset && assetFlags.has('crossout');
    const shouldShowVipPrice = shouldShowPromoPrice ? false : !bundle;

    const promoLabelMessage = useMemo(() => {
      if (elementsToHide.promo) {
        return null;
      }

      if (asset) {
        return (
          <PromoLabel
            isSaleItem={isSaleItem}
            $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
          >
            <PromoMessageAsset asset={asset} />
          </PromoLabel>
        );
      }

      if (shouldShowPromoPrice && isDowngraded) {
        return (
          <PromoLabel
            isSaleItem={isSaleItem}
            $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
          >
            {promos.promo_label}
          </PromoLabel>
        );
      }

      return shouldShowPromoPrice ? (
        <PromoLabel
          isSaleItem={isSaleItem}
          $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
        >
          <DefaultPromoMessage isVip={isVip} isSet={isSet} />
        </PromoLabel>
      ) : null;
    }, [
      elementsToHide,
      asset,
      gridPdpPricingUpdatesEnabled,
      isDowngraded,
      isSaleItem,
      isSet,
      isVip,
      promos,
      shouldShowPromoPrice,
    ]);

    // Only generate custom model images if `modelIndex` is not the default model
    const modelImages =
      isMatchUserSizeFeatureEnabled && initialModel?.modelIndex > 0
        ? generateCdnImagesBmig(product, initialModel.modelIndex)
        : null;

    const handleQuickViewClick = useCallback(
      e => {
        if (isQuickViewEnabled) {
          e.preventDefault();
          e.stopPropagation();
          return setDefaultProductAndOpenQuickViewModal();
        }
      },
      [isQuickViewEnabled, setDefaultProductAndOpenQuickViewModal]
    );

    return (
      <StyledBaseProductCell
        {...rest}
        product={{
          ...product,
          ...(modelImages ? { productImages: modelImages } : null),
        }}
        ref={mergeRefs([updateProductCellRef, ref])}
        onClick={handleQuickViewClick}
      >
        <ProductLink
          href={href}
          aria-label={product.label}
          onClick={handleClick}
        >
          <ThumbnailWrapper>
            <StyledProductImage
              aria-label={product.label}
              aspectRatio={imageAspectRatio}
              role="img"
              primaryAngle={
                shouldShowSpacedBorder ||
                shouldShowByoLayout ||
                shouldShowLaydown
                  ? 'LAYDOWN'
                  : 1
              }
              secondaryAngle={
                shouldShowSpacedBorder || shouldShowLaydown ? 1 : 'LAYDOWN'
              }
              disableHover={shouldShowSpacedBorder || shouldShowByoLayout}
              type={imageType}
              breakpoints={[
                { minWidth: 0, maxWidth: 960, pictureWidth: 200 },
                { minWidth: 960, maxWidth: 1200, pictureWidth: 300 },
                { minWidth: 1200, pictureWidth: 400 },
              ]}
              loading="lazy"
            />
          </ThumbnailWrapper>
          {!elementsToHide.overlay && isSoldOut && !isComingSoonItem ? (
            <SoldOutOverlay>
              <FormattedMessage
                id="global_checkout.sold_out"
                defaultMessage="Sold Out"
              />
            </SoldOutOverlay>
          ) : null}
          {!elementsToHide.overlay && isComingSoonItem ? (
            <ComingSoonOverlay>
              <FormattedMessage
                id="global_checkout.coming_soon_label"
                defaultMessage="Coming Soon"
              />
            </ComingSoonOverlay>
          ) : null}
        </ProductLink>

        {!elementsToHide.wishlist ? (
          <ImageOverlay
            {...(constructorSearchEnabled && isCio
              ? {
                  'data-cnstrc-btn': 'add-to-favorties',
                }
              : {})}
          >
            <FavoritesToggleButton masterProductId={product.masterProductId} />
          </ImageOverlay>
        ) : null}

        {isQuickViewEnabled ? (
          <QuickShopLabel
            data-autotag="quick-shop-label"
            data-qa-automation="QuickShopLabel"
          >
            <FormattedMessage
              id="site_pdp.quick_shop"
              defaultMessage="QUICK SHOP"
            />
          </QuickShopLabel>
        ) : null}

        <ProductSection>
          {shouldShowLocked ? (
            <LockedProducts>
              <FaLock />
            </LockedProducts>
          ) : null}
          {!isProductPreorder &&
          !elementsToHide.reviewStars &&
          (isVip || isLead) ? (
            <ProductLink href={href} onClick={handleClick}>
              <ProductReview
                emptyIcon={<EmptyStar />}
                fullIcon={<FullStar />}
                data-qa-automation="gridItemStars"
                ratingStyle={ratingStyle}
                hideReviewCount
              />
            </ProductLink>
          ) : null}
          <ProductLink href={href} tabIndex={-1} onClick={handleClick}>
            {shouldDisplayNewProductPricing ? (
              <PriceWrapper
                $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
              >
                {!elementsToHide.name && (
                  <ProductName
                    data-qa-automation="gridProdName"
                    $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
                  />
                )}
                <ProductPrice
                  memberPrice={product.defaultUnitPrice}
                  guestPrice={product.retailUnitPrice}
                  isVip={isVip}
                />
              </PriceWrapper>
            ) : (
              <CellDetailsWrapper>
                {promoLabelMessage}
                {!elementsToHide.name && (
                  <ProductName
                    data-qa-automation="gridProdName"
                    $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
                  />
                )}
                {!elementsToHide.price ? (
                  <PriceWrapper
                    $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
                  >
                    {isBorderfreeCustomer ? (
                      <BorderfreePricing>
                        <BorderFreePrice
                          amount={displayPrice}
                          isPromoPrice={shouldShowPromoPrice}
                          isBorderfreeCustomer
                        />
                        <USDPrice>
                          / USD
                          <span>${displayPrice}</span>
                        </USDPrice>
                      </BorderfreePricing>
                    ) : displayPrice ? (
                      <StyledProductPrice
                        showPromoPrice={shouldShowPromoPrice}
                        showVipPrice={shouldShowVipPrice}
                        priceStyle={priceStyle(isSaleItem)}
                        promoPriceStyle={promoPriceStyle(isSaleItem)}
                        isSaleItem={isSaleItem}
                        strikeRegularPrice={crossOut}
                      />
                    ) : null}

                    {gridPdpPricingUpdatesEnabled &&
                    (shouldShowPromoPrice || isVip) &&
                    !overlayText ? (
                      <StyledProductPrice
                        showPromoPrice={false}
                        showVipPrice={false}
                        showRetailPrice={isVip ? !shouldShowPromoPrice : true}
                        strikeRegularPrice
                        priceStyle={priceStyle(isSaleItem)}
                        promoPriceStyle={promoPriceStyle(isSaleItem)}
                        isSaleItem={isSaleItem}
                      />
                    ) : null}

                    {overlayText && !isByo ? (
                      <>
                        <BundlePromoWrapper
                          $gridPdpPricingUpdatesEnabled={
                            gridPdpPricingUpdatesEnabled
                          }
                        >
                          <>
                            <OptionText
                              $gridPdpPricingUpdatesEnabled={
                                gridPdpPricingUpdatesEnabled
                              }
                            >
                              <FormattedMessage
                                id="site_pdp.or"
                                defaultMessage="or"
                              />
                            </OptionText>
                            <BundlePromoLabel
                              dangerouslySetInnerHTML={{
                                __html: overlayText,
                              }}
                            />
                          </>
                        </BundlePromoWrapper>
                        {gridPdpPricingUpdatesEnabled &&
                        shouldShowPromoPrice ? (
                          <StyledProductPrice
                            showPromoPrice={false}
                            showVipPrice={false}
                            showRetailPrice={!isVip}
                            strikeRegularPrice
                            priceStyle={priceStyle(isSaleItem)}
                            promoPriceStyle={promoPriceStyle(isSaleItem)}
                            isSaleItem={isSaleItem}
                          />
                        ) : null}
                      </>
                    ) : null}
                  </PriceWrapper>
                ) : null}
              </CellDetailsWrapper>
            )}
          </ProductLink>

          {!elementsToHide.colorsAvailable ? (
            <MoreColorsLink
              href={href}
              tabIndex={-1}
              onClick={handleClick}
              data-qa-automation="gridItemMoreColorsLabel"
            >
              <FormattedMessage
                defaultMessage="+ More Colors"
                id="site_pdp.color_swatch_label_colors"
              />
            </MoreColorsLink>
          ) : null}
          {renderCallout(
            isProductPreorder,
            shouldProductUseCustom,
            elementsToHide,
            callout
          )}
        </ProductSection>
        {children ? (
          <AdditionalContent>
            <ProductLink
              href={additionalContentHref || href}
              tabIndex={additionalContentHref ? undefined : -1}
              onClick={handleClick}
            >
              {children}
            </ProductLink>
          </AdditionalContent>
        ) : null}
      </StyledBaseProductCell>
    );
  }
);

ProductCellBMIG.displayName = 'ProductCellBMIG';

ProductCellBMIG.propTypes = {
  additionalContentHref: PropTypes.object,
  aggregationFilter: PropTypes.object,
  children: PropTypes.node,
  elementsToHide: PropTypes.object,
  fplId: PropTypes.number,
  gridIndex: PropTypes.number,
  href: PropTypes.string.isRequired,
  imageAspectRatio: PropTypes.number,
  imageType: PropTypes.oneOf(['default', 'plus']),
  isQuickViewEnabled: PropTypes.bool,
  onProductClick: PropTypes.func,
  overridHref: PropTypes.string,
  payloadKey: PropTypes.string,
  product: PropTypes.object.isRequired,
  productSource: PropTypes.string,
  setDefaultProductAndOpenQuickViewModal: PropTypes.func,
  shouldOverrideHref: PropTypes.bool,
  shouldShowByoLayout: PropTypes.bool,
  shouldShowLaydown: PropTypes.bool,
  shouldShowLocked: PropTypes.bool,
  shouldShowShadow: PropTypes.bool,
  shouldShowSpacedBorder: PropTypes.bool,
  tabIndex: PropTypes.number,
};

ProductCellBMIG.Callout = Callout;
ProductCellBMIG.MoreColorsLink = MoreColorsLink;
ProductCellBMIG.ProductImage = StyledProductImage;
ProductCellBMIG.ProductSection = ProductSection;
ProductCellBMIG.ThumbnailWrapper = ThumbnailWrapper;
ProductCellBMIG.ProductName = ProductName;
ProductCellBMIG.PriceWrapper = PriceWrapper;
ProductCellBMIG.ProductPrice = StyledProductPrice;
ProductCellBMIG.PromoLabel = PromoLabel;
ProductCellBMIG.ProductLink = ProductLink;
ProductCellBMIG.QuickShopLabel = QuickShopLabel;
ProductCellBMIG.OptionText = OptionText;
ProductCellBMIG.BundlePromoLabel = BundlePromoLabel;

// Fixes Property <extra component> does not exist on type
// 'ForwardRefExoticComponent<RefAttributes<any>>'
const ProductCellBMIGNamespace = Object.assign(ProductCellBMIG, {
  Callout,
  MoreColorsLink,
  StyledProductImage,
  ProductSection,
  ThumbnailWrapper,
  ProductName,
  PriceWrapper,
  PromoLabel,
  ProductLink,
  QuickShopLabel,
});

export default ProductCellBMIGNamespace;
